import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, Loader2, Users, Lock, Server, X } from 'lucide-react';
import { email, dataHora } from './api';

const VAZIA = { id: null, endereco: '', nome: '', tipo: 'pessoal', senha: '', membros: [], assinatura: '', ativa: true, imap_host: 'imap.skymail.net.br', smtp_host: 'smtp.skymail.net.br', senhaEnv: '', diasIniciais: 30 };

function EscolherMembros({ usuarios, valor, onChange }) {
  const [busca, setBusca] = useState('');
  const achados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (t.length < 2) return [];
    return usuarios.filter(u => !valor.includes(u.email) && (u.nome.toLowerCase().includes(t) || u.email.includes(t))).slice(0, 8);
  }, [busca, usuarios, valor]);
  return (
    <div className="em-membros">
      <div className="em-chips">
        {valor.map(m => (
          <span key={m} className="em-chip">{usuarios.find(u => u.email === m)?.nome || m}
            <button type="button" onClick={() => onChange(valor.filter(x => x !== m))}><X size={12} /></button>
          </span>
        ))}
        <input placeholder="Buscar usuário do Quality…" value={busca} onChange={e => setBusca(e.target.value)} />
      </div>
      {achados.length > 0 && (
        <div className="em-sugestoes em-sugestoes-estatica">
          {achados.map(u => <button type="button" key={u.email} onClick={() => { onChange([...valor, u.email]); setBusca(''); }}><strong>{u.nome}</strong> <span>{u.email}</span></button>)}
        </div>
      )}
    </div>
  );
}

/** Cadastro das caixas de e-mail: endereço, senha (testada antes de gravar), quem vê, assinatura. */
export default function Caixas({ eu, onMudou }) {
  const [dados, setDados] = useState(null);
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const [avancado, setAvancado] = useState(false);

  const carregar = () => email('caixas').then(d => {
    setDados(d);
    if (!d.caixas.length) setForm({ ...VAZIA, endereco: '', membros: [eu.email] });
  }).catch(e => setErro(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);

  const editar = c => { setErro(''); setOk(''); setAvancado(false); setForm({ ...VAZIA, ...c, senha: '', diasIniciais: c.diasIniciais || 30 }); };
  const campo = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function salvar(e) {
    e.preventDefault();
    setErro(''); setOk(''); setSalvando(true);
    try {
      const r = await email('caixaSalvar', { ...form, senha: form.senha || undefined });
      setOk(`Caixa ${r.caixa.endereco} salva${form.senha ? ' — usuário e senha conferidos com o servidor' : ''}.${!r.caixa.estado?.ultimaLeitura ? ' O robô já começou a ler os e-mails dos últimos 30 dias (ligando pacientes, prazos e reuniões) — numa caixa grande leva alguns minutos.' : ''}`);
      setForm(null);
      await carregar();
      onMudou?.();
    } catch (e2) { setErro(e2.message); } finally { setSalvando(false); }
  }
  async function excluir(c) {
    if (!window.confirm(`Remover a caixa ${c.endereco} do Quality?\n\nOs e-mails continuam na caixa (Skymail); só some daqui, junto com as ligações a pacientes e prazos marcados.`)) return;
    try { await email('caixaExcluir', { id: c.id }); setForm(null); await carregar(); onMudou?.(); } catch (e) { setErro(e.message); }
  }

  if (!dados) return <div className="em-vazio">{erro || 'Carregando caixas…'}</div>;
  const admin = dados.admin;

  return (
    <div className="em-caixas">
      <div className="em-caixas-cab">
        <div>
          <h2>Caixas de e-mail</h2>
          <p className="em-sub">Cada caixa aparece só para quem é membro dela. A senha fica guardada cifrada no servidor do Quality e é conferida com a Skymail antes de gravar.</p>
        </div>
        <button className="em-btn em-btn-primario" onClick={() => editar({ ...VAZIA, membros: [eu.email] })}><Plus size={15} /> Conectar caixa</button>
      </div>
      {ok && <div className="em-aviso em-aviso-ok">{ok}<button onClick={() => setOk('')}><X size={14} /></button></div>}
      {erro && !form && <div className="em-aviso em-aviso-erro">{erro}<button onClick={() => setErro('')}><X size={14} /></button></div>}

      <div className="em-caixas-lista">
        {dados.caixas.map(c => (
          <div key={c.id} className={`em-caixa-item ${form?.id === c.id ? 'ativa' : ''}`}>
            <div className="em-caixa-info">
              <strong>{c.nome}</strong> <span className="em-sub">{c.endereco}</span>
              <div className="em-tags">
                <span className={`em-tag ${c.tipo === 'compartilhada' ? 'em-tag-azul' : ''}`}>{c.tipo === 'compartilhada' ? 'Compartilhada' : 'Pessoal'}</span>
                {!c.ativa && <span className="em-tag em-tag-cinza">Desligada</span>}
                {!c.temSenha && <span className="em-tag em-tag-vermelha">Sem senha aqui</span>}
                {c.estado?.erro && <span className="em-tag em-tag-vermelha" title={c.estado.erro}>Erro na última leitura</span>}
                {c.estado?.cota && <span className={`em-tag ${c.estado.cota.pct >= 85 ? 'em-tag-vermelha' : ''}`}>Caixa {c.estado.cota.pct}% cheia</span>}
              </div>
              <div className="em-sub">
                <Users size={12} /> {c.membros.length ? c.membros.map(m => dados.usuarios.find(u => u.email === m)?.nome || m).join(', ') : 'ninguém ainda'}
                {c.estado?.ultimaLeitura && <> · lida pelo robô em {dataHora(c.estado.ultimaLeitura)}</>}
              </div>
            </div>
            <div className="em-caixa-acoes">
              {c.podeEditar && <button className="em-btn" onClick={() => editar(c)}>Editar</button>}
              {c.podeExcluir && <button className="em-btn em-btn-perigo" onClick={() => excluir(c)} title="Remover do Quality"><Trash2 size={14} /></button>}
            </div>
          </div>
        ))}
        {!dados.caixas.length && !form && <div className="em-vazio">Nenhuma caixa conectada ainda.</div>}
      </div>

      {form && (
        <form className="em-form" onSubmit={salvar}>
          <h3>{form.id ? `Editar ${form.endereco}` : 'Conectar caixa'}</h3>
          <div className="em-form-grade">
            <label>E-mail da caixa
              <input type="email" required value={form.endereco} disabled={!!form.id} onChange={e => campo('endereco', e.target.value)} placeholder="nome@vitalcarehc.com.br" />
            </label>
            <label>Nome que aparece
              <input value={form.nome} onChange={e => campo('nome', e.target.value)} placeholder="Ex.: Captação Vitalcare" />
            </label>
            <label><span><Lock size={12} /> Senha da caixa</span>
              <input type="password" autoComplete="new-password" value={form.senha} onChange={e => campo('senha', e.target.value)}
                placeholder={form.id ? (form.temSenha ? 'deixe em branco para manter' : 'informe a senha') : 'senha do e-mail'} required={!form.id && !form.senhaEnv} />
            </label>
            {admin && (
              <label>Tipo
                <select value={form.tipo} onChange={e => campo('tipo', e.target.value)}>
                  <option value="pessoal">Pessoal (de uma pessoa — convites vão para a agenda dela)</option>
                  <option value="compartilhada">Compartilhada (setor — várias pessoas veem)</option>
                </select>
              </label>
            )}
          </div>
          {(form.tipo === 'compartilhada' && admin) || dados.superadmin ? (
            <label className="em-form-linha"><span><Users size={12} /> Quem vê esta caixa (pelo login no Quality) — clique no nome para incluir</span>
              <EscolherMembros usuarios={dados.usuarios} valor={form.membros} onChange={v => campo('membros', v)} />
            </label>
          ) : (
            <div className="em-form-linha em-sub"><Lock size={12} /> Caixa pessoal: só você ({eu.email}) vê. Nenhum outro usuário — nem administrador — enxerga esta caixa.</div>
          )}
          <label className="em-form-linha">Assinatura (vai no fim dos e-mails escritos pelo Quality)
            <textarea rows={4} value={form.assinatura} onChange={e => campo('assinatura', e.target.value)} placeholder={'Alexandre Barea\nCoordenação Administrativa — Vitalcare\n(21) 0000-0000'} />
          </label>
          <label className="em-check"><input type="checkbox" checked={form.ativa} onChange={e => campo('ativa', e.target.checked)} /> Caixa ligada (o robô lê os e-mails novos a cada 2 minutos)</label>
          <button type="button" className="em-link-copias" onClick={() => setAvancado(a => !a)}><Server size={12} /> {avancado ? 'Esconder' : 'Mostrar'} opções avançadas</button>
          {avancado && (
            <div className="em-form-grade">
              <label>Servidor de entrada (IMAP)<input value={form.imap_host} onChange={e => campo('imap_host', e.target.value)} /></label>
              <label>Servidor de saída (SMTP)<input value={form.smtp_host} onChange={e => campo('smtp_host', e.target.value)} /></label>
              <label>Primeira leitura: últimos (dias)<input type="number" min={1} max={365} value={form.diasIniciais} onChange={e => campo('diasIniciais', e.target.value)} /></label>
              {admin && <label>Senha em variável do servidor (opcional)<input value={form.senhaEnv} onChange={e => campo('senhaEnv', e.target.value)} placeholder="ex.: CAPTACAO_IMAP_SENHA_1" /></label>}
            </div>
          )}
          {erro && <div className="em-aviso em-aviso-erro">{erro}</div>}
          <div className="em-form-botoes">
            <button className="em-btn em-btn-primario" disabled={salvando}>{salvando ? <Loader2 size={15} className="em-gira" /> : <Save size={15} />} {salvando ? 'Conferindo com o servidor…' : 'Salvar'}</button>
            <button type="button" className="em-btn" onClick={() => { setForm(null); setErro(''); }}>Cancelar</button>
          </div>
        </form>
      )}
    </div>
  );
}
